import { io, type Socket } from 'socket.io-client'
import { commandAckSchema, connectErrorDataSchema, type CommandAck } from './contract'
import { StatusEmitter, type RealtimeConnectOptions, type RealtimeTransport } from './transport'

const ACK_TIMEOUT_MS = 5_000

/**
 * Conexão Socket.IO única, autenticada com `auth.token` (socketio-contracts.md §11):
 * - `auth` é função: cada (re)conexão usa o token atual;
 * - `connect_error` com `reason=token_expired` → renova o token e reconecta;
 * - `forbidden` → status `forbidden` (o app cai para polling);
 * - `io server disconnect` (token expirou no servidor) → renova e reconecta.
 */
export function createSocketTransport(): RealtimeTransport {
  let socket: Socket | null = null
  const status = new StatusEmitter()
  const handlers = new Map<string, Set<(payload: unknown) => void>>()

  const bind = (target: Socket, event: string) => {
    target.on(event, (payload: unknown) =>
      handlers.get(event)?.forEach((handler) => handler(payload)),
    )
  }

  return {
    connect({ url, getToken, onAuthExpired }: RealtimeConnectOptions) {
      if (socket) return
      status.set('connecting')
      const current = io(url, {
        transports: ['websocket'],
        auth: (callback) => callback({ token: getToken() ?? '' }),
        reconnectionDelay: 1_000,
        reconnectionDelayMax: 15_000,
        timeout: 10_000,
      })
      socket = current
      const renewAndReconnect = () => {
        void onAuthExpired()
          .catch(() => undefined)
          .finally(() => {
            if (socket === current) current.connect()
          })
      }
      current.on('connect', () => status.set('connected'))
      current.on('disconnect', (reason) => {
        status.set('disconnected')
        if (reason === 'io server disconnect') renewAndReconnect()
      })
      current.on('connect_error', (error: Error & { data?: unknown }) => {
        const data = connectErrorDataSchema.safeParse(error.data)
        if (data.success && data.data.code === 'forbidden') {
          status.set('forbidden')
          current.disconnect()
          return
        }
        status.set('error')
        if (data.success && data.data.reason === 'token_expired') renewAndReconnect()
      })
      handlers.forEach((_, event) => bind(current, event))
    },
    disconnect() {
      socket?.removeAllListeners()
      socket?.disconnect()
      socket = null
      status.set('idle')
    },
    on(event, handler) {
      let set = handlers.get(event)
      if (!set) {
        set = new Set()
        handlers.set(event, set)
        if (socket) bind(socket, event)
      }
      set.add(handler)
      return () => {
        set.delete(handler)
      }
    },
    onStatus: (listener) => status.subscribe(listener),
    async command(name, payload): Promise<CommandAck> {
      if (!socket?.connected) return { ok: false, error: { code: 'not_connected' } }
      try {
        const ack: unknown = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck(name, payload)
        const parsed = commandAckSchema.safeParse(ack)
        return parsed.success ? parsed.data : { ok: false, error: { code: 'invalid_ack' } }
      } catch {
        return { ok: false, error: { code: 'timeout' } }
      }
    },
  }
}
